// MacroBar — MacroFactor-style horizontal progress bar with a goal marker
// at the target line. One macro at a time (protein/fat/carbs); kcal uses
// the larger MacroRing.
//
// When consumed > target * 1.05 the over portion extends past the goal
// marker in a warn tint, so the user sees both "I hit my target" and "I
// went this much past it" in one glance.

export function MacroBar({
  label,
  consumed,
  target,
  unit = 'g',
  height = 6,
}: {
  label: string;
  consumed: number;
  target: number;
  unit?: string;
  height?: number;
}) {
  // The visible range goes 0–130% of target so a moderate overshoot still
  // fits the bar without clipping. Anything past 130% is implicitly clamped
  // (the user notices via the textual count).
  const ratio = target > 0 ? consumed / target : 0;
  const fillPct = Math.min(ratio, 1) * (100 / 1.3); // up to ~76.9% width = 100% target
  const overPct =
    ratio > 1 ? Math.min(ratio - 1, 0.3) * (100 / 1.3) : 0;
  // 100% target sits at 76.9% of the visible track — that's where the goal
  // marker drops.
  const goalPct = 100 / 1.3;
  const isOver = consumed > target * 1.05;
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint">
          {label}
        </span>
        <span className="font-mono text-xs tabular-nums text-fg">
          {consumed}
          <span className="text-fg-faint"> / {target}{unit}</span>
          {isOver && (
            <span className="text-warn ml-1">+{consumed - target}</span>
          )}
        </span>
      </div>
      <div
        className="relative w-full bg-bg-sunken rounded-full"
        style={{ height }}
        role="meter"
        aria-valuemin={0}
        aria-valuemax={target}
        aria-valuenow={consumed}
        aria-label={`${label}: ${consumed} of ${target} ${unit}`}
      >
        {/* Fill from 0 up to the target line — flat green, no gradient and no
            gloss. The bar is data, not a surface. */}
        <div
          className="absolute inset-y-0 left-0 rounded-full transition-all duration-300 ease-out"
          style={{
            width: `${fillPct}%`,
            background: 'var(--color-good-mid)',
          }}
        />
        {/* Over-target portion past the goal marker */}
        {overPct > 0 && (
          <div
            className="absolute inset-y-0 bg-warn-mid transition-all duration-300 ease-out"
            style={{ left: `${goalPct}%`, width: `${overPct}%` }}
          />
        )}
        {/* Goal marker — vertical hairline at the target tick */}
        <div
          aria-hidden
          className="absolute -top-0.5 -bottom-0.5 w-px bg-fg-faint"
          style={{ left: `${goalPct}%` }}
        />
      </div>
    </div>
  );
}
